import { DollarSign, FileText, Calendar, Car, TrendingUp, AlertTriangle, Clock, Zap, IdCard, ListChecks } from 'lucide-react';
import { KpiCard } from '@/components/KpiCard';
import { StatusDot } from '@/components/StatusBadge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { timeAgo } from '@/data/mockData';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { STAGE_ORDER, STAGE_LABELS, DEMO_TYPE_LABELS, NEXT_STEP_LABELS } from '@/types';
import type { ActivityType } from '@/types';
import { Link } from 'react-router-dom';
import { PilotUserList } from '@/components/PilotUserList';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { RecentConversationsCard } from '@/components/comms/RecentConversationsCard';
import { ConversationsInsightsCard } from '@/components/comms/ConversationsInsightsCard';

function actLabel(t: ActivityType) {
  if (t in DEMO_TYPE_LABELS) return DEMO_TYPE_LABELS[t as keyof typeof DEMO_TYPE_LABELS];
  const map: Record<string, string> = { text_sent: 'Text Sent', email_sent: 'Email Sent', note_added: 'Note Added' };
  return map[t] || t;
}

export default function ManagerDashboard() {
  const { isSolo, mode } = useAuth();
  const { deals, activities, customerFeedback, floorReps, loading } = useDealershipData();
  // Solo workspaces (single-rep accounts) should land on the rep view.
  if (mode === 'real' && isSolo) return <Navigate to="/rep" replace />;
  const recentActs = activities.slice(0, 8);
  const closingDeals = deals.filter(d => d.stage === 'closing');
  const stalledDeals = deals
    .filter(d => d.status === 'no_activity' || d.status === 'slowing')
    .sort((a, b) => new Date(a.last_activity_at).getTime() - new Date(b.last_activity_at).getTime());
  const redFeedback = customerFeedback.filter(f => f.rating_color === 'red');
  const noNextStep = deals.filter(d => !d.next_step && d.stage !== 'sold');

  const dealsActive = deals.filter(d => d.stage !== 'sold').length;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isToday = (iso: string) => new Date(iso) >= today;
  const demosToday = activities.filter(a =>
    isToday(a.created_at) &&
    ['walkaround', 'test_drive', 'quote_review', 'followup_video'].includes(a.activity_type),
  ).length;
  const soldToday = deals.filter(d => d.stage === 'sold' && isToday(d.last_activity_at)).length;
  const estimatedRevenue = closingDeals.reduce((s, d) => s + d.estimated_value, 0);

  // New-flow metrics: Quick Capture adoption + outstanding next-steps
  const capturesToday = deals.filter(d => isToday(d.created_at));
  const actionsToday = activities.filter(a => isToday(a.created_at)).length;
  const openNextSteps = deals.filter(d => d.next_step && d.stage !== 'sold').length;
  const licenseScansToday = capturesToday.filter(d => !!d.license_image_path).length;

  // Yesterday comparisons for trend arrows
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const isYesterday = (iso: string) => { const d = new Date(iso); return d >= yest && d < today; };
  const demosYesterday = activities.filter(a =>
    isYesterday(a.created_at) &&
    ['walkaround', 'test_drive', 'quote_review', 'followup_video'].includes(a.activity_type),
  ).length;
  const actionsYesterday = activities.filter(a => isYesterday(a.created_at)).length;
  const capturesYesterday = deals.filter(d => isYesterday(d.created_at)).length;
  const pctDelta = (curr: number, prev: number) => {
    if (prev === 0) return curr === 0 ? { dir: 'flat' as const, label: 'No change vs yesterday' } : { dir: 'up' as const, label: 'New today' };
    const pct = Math.round(((curr - prev) / prev) * 100);
    if (pct === 0) return { dir: 'flat' as const, label: 'Flat vs yesterday' };
    return { dir: pct > 0 ? ('up' as const) : ('down' as const), label: `${pct > 0 ? '+' : ''}${pct}% vs yesterday` };
  };
  const demosDelta = pctDelta(demosToday, demosYesterday);
  const actionsDelta = pctDelta(actionsToday, actionsYesterday);
  const capturesDelta = pctDelta(capturesToday.length, capturesYesterday);

  const alertCount = stalledDeals.length + noNextStep.length + redFeedback.length;

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      <div>
        <h1 className="text-2xl font-bold">Sales Floor Command Center</h1>
        <p className="text-sm text-muted-foreground">Real-time visibility into your dealership floor</p>
      </div>

      <OnboardingChecklist />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Deals Active"
          value={dealsActive}
          icon={FileText}
          tone={dealsActive === 0 ? 'critical' : dealsActive < 3 ? 'attention' : 'healthy'}
        />
        <KpiCard
          label="Captures Today"
          value={capturesToday.length}
          icon={Zap}
          trend={licenseScansToday > 0 ? `${licenseScansToday} with license scan` : capturesDelta.label}
          trendDirection={capturesDelta.dir}
        />
        <KpiCard
          label="Actions Logged Today"
          value={actionsToday}
          icon={ListChecks}
          trend={actionsDelta.label}
          trendDirection={actionsDelta.dir}
        />
        <KpiCard
          label="Open Next Steps"
          value={openNextSteps}
          icon={Calendar}
          tone={openNextSteps > 10 ? 'attention' : 'neutral'}
          trendPolarity="bad"
        />
        <KpiCard
          label="Demos Today"
          value={demosToday}
          icon={Car}
          trend={demosDelta.label}
          trendDirection={demosDelta.dir}
          tone={demosToday === 0 ? 'attention' : 'healthy'}
        />
        <KpiCard
          label="Sold Today"
          value={soldToday}
          icon={TrendingUp}
          tone={soldToday > 0 ? 'healthy' : 'neutral'}
        />
        <KpiCard
          label="Est. Revenue"
          value={`$${(estimatedRevenue / 1000).toFixed(0)}K`}
          icon={DollarSign}
          trend="In closing stage"
        />
        <KpiCard
          label="Alerts"
          value={alertCount}
          icon={AlertTriangle}
          tone={alertCount === 0 ? 'healthy' : alertCount > 5 ? 'critical' : 'attention'}
          trend={alertCount === 0 ? 'All clear' : 'Needs attention'}
          trendPolarity="bad"
        />
      </div>

      {!loading && deals.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <p className="text-sm">No deals yet. Once your team starts logging activity, your floor will come alive here.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Floor Activity + Pipeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Floor Activity Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Floor Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rep Name</TableHead>
                    <TableHead className="text-center">Active</TableHead>
                    <TableHead className="text-center">Demos</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead className="text-center">Hot</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {floorReps.map(rep => (
                    <TableRow key={rep.id}>
                      <TableCell className="font-medium">{rep.name}</TableCell>
                      <TableCell className="text-center">{rep.active_deals}</TableCell>
                      <TableCell className="text-center">{rep.demos}</TableCell>
                      <TableCell className="text-muted-foreground">{timeAgo(rep.last_activity)}</TableCell>
                      <TableCell className="text-center">{rep.hot_deals > 0 ? <span className="text-red-400 font-medium">{rep.hot_deals}</span> : '—'}</TableCell>
                      <TableCell><StatusDot status={rep.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pipeline Kanban */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                {STAGE_ORDER.map(stage => {
                  const stageDeals = deals.filter(d => d.stage === stage);
                  return (
                    <div key={stage} className="min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{STAGE_LABELS[stage]}</span>
                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{stageDeals.length}</span>
                      </div>
                      <div className="space-y-2">
                        {stageDeals.slice(0, 3).map(deal => (
                          <Link key={deal.id} to={`/deals/${deal.id}`} className="block">
                            <div className="bg-muted/50 border border-border rounded-md p-3 hover:border-primary/30 transition-colors">
                              <p className="text-sm font-medium truncate">{deal.customer_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{deal.vehicle}</p>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-muted-foreground">{deal.rep_name.split(' ')[0]}</span>
                                <span className="text-xs font-medium">${(deal.estimated_value / 1000).toFixed(0)}K</span>
                              </div>
                              {deal.next_step && <p className="text-xs text-primary mt-1">Next: {NEXT_STEP_LABELS[deal.next_step]}</p>}
                              {!deal.next_step && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No next step!</p>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Top Opportunities */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Top Opportunities — Closing Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {closingDeals.map(deal => (
                  <Link key={deal.id} to={`/deals/${deal.id}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                    <div>
                      <p className="font-medium">{deal.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{deal.vehicle} · {deal.rep_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400">${deal.estimated_value.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{timeAgo(deal.last_activity_at)}</p>
                    </div>
                  </Link>
                ))}
                <div className="pt-2 border-t border-border flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total in closing</span>
                  <span className="font-bold text-lg">${closingDeals.reduce((s, d) => s + d.estimated_value, 0).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Alerts Panel — wedge feature, visually dominant */}
          <Card className="lg:sticky lg:top-20 border-red-500/40 bg-gradient-to-b from-red-500/[0.04] to-transparent shadow-[0_0_40px_-15px_hsl(0_85%_55%/0.45)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <AlertTriangle className="h-4 w-4 text-red-400" /> Alerts
                {alertCount > 0 && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 font-semibold">{alertCount}</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alertCount === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">All clear. Nothing needs your attention right now.</p>
                )}
                {stalledDeals.map(deal => (
                  <Link key={deal.id} to={`/deals/${deal.id}`} className="flex items-start gap-3 p-2 rounded hover:bg-muted/30 transition-colors">
                    <Clock className={`h-4 w-4 mt-0.5 shrink-0 ${deal.status === 'no_activity' ? 'text-red-400' : 'text-yellow-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{deal.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{deal.status === 'no_activity' ? 'No activity' : 'Slowing down'} · {deal.rep_name.split(' ')[0]}</p>
                    </div>
                  </Link>
                ))}
                {noNextStep.map(deal => (
                  <Link key={`ns-${deal.id}`} to={`/deals/${deal.id}`} className="flex items-start gap-3 p-2 rounded hover:bg-muted/30 transition-colors">
                    <AlertTriangle className="h-4 w-4 mt-0.5 text-red-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{deal.customer_name}</p>
                      <p className="text-xs text-red-400">Missing next step!</p>
                    </div>
                  </Link>
                ))}
                {redFeedback.map(fb => (
                  <div key={fb.id} className="flex items-start gap-3 p-2 rounded bg-red-500/5">
                    <div className="w-4 h-4 rounded-full bg-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-400">Red Feedback Alert</p>
                      <p className="text-xs text-muted-foreground">{fb.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Live Activity Feed */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse-soft" /> Live Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActs.map(act => (
                  <div key={act.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-xs font-bold text-primary">
                      {act.rep_name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm"><span className="font-medium">{act.rep_name.split(' ')[0]}</span> <span className="text-muted-foreground">{actLabel(act.activity_type).toLowerCase()}</span></p>
                      {act.customer_name && <p className="text-xs text-muted-foreground truncate">{act.customer_name}</p>}
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(act.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Email + SMS conversations (real mode only) */}
          <RecentConversationsCard />

          {/* Conversations insights — avg reply time, active threads, awaiting reply */}
          <ConversationsInsightsCard />
        </div>

        {/* Team Members — manager-only, real-mode only (component self-hides) */}
        <PilotUserList />
      </div>
    </div>
  );
}
